import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { useNavigateToAgentsEditor } from '@/hooks/navigation/useNavigateToAgentsEditor';
import Close from '@/images/Close.svg';
import Warning from '@/images/WarningColored.svg';
import { useUpdateAgent } from '@/pages/agents/api';
import { useAnalytics } from '@/services/analytics';
import theme from '@/theme';
import { IAgent } from '@d-id/types';
import styled from '@emotion/styled';
import { Button, Dialog } from '@mui/material';
import { useEffect } from 'react';

const StyledDialog = styled(Dialog)`
    .MuiDialog-paper {
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        padding: 40px;
        font-family: 'Manrope', sans-serif;
        color: ${theme.colors.black[800]};
        gap: 16px;
    }
`;

const Text = styled.h2`
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: -0.48px;
    color: ${theme.colors.secondary.main};
`;

const Title = styled.div`
    display: flex;
    justify-content: start;
    align-items: center;
    gap: 10px;

    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.48px;
`;

const CloseButton = styled(MaskedIcon)`
    position: absolute;
    right: 16px;
    top: 16px;
`;

const MainText = styled.div`
    color: ${theme.colors.secondary['main']};
    font-size: 1.6rem;
    font-weight: 400;
    line-height: normal;
    letter-spacing: 0.15px;
`;

const StyledIcon = styled.img``;

const StyledButton = styled(Button)<{ inverted?: boolean }>`
    background-color: ${({ inverted }) => (inverted ? 'white' : theme.colors.secondary.main)};
    color: ${({ inverted }) => (inverted ? theme.colors.secondary.main : 'white')};
    border: 1px solid ${theme.colors.secondary.main};
    width: 260px;
    border-radius: 12px;
    display: flex;
    padding: 10px 0px;
    justify-content: center;
    align-items: center;
    text-transform: capitalize;
    &:hover {
        background-color: ${({ inverted }) => (inverted ? '#eee' : theme.colors.secondary['700'])};
        color: ${({ inverted }) => (inverted ? theme.colors.secondary['700'] : '#eee')};
    }
`;

interface Props {
    agent?: IAgent;
    isOpen?: boolean;
    onClose: () => void;
}

export const RemoveEmbedAgentModal = ({ isOpen = false, onClose, agent }: Props) => {
    const mixpanel = useAnalytics();
    const { mutateAsync: updateAgentMutation } = useUpdateAgent();
    const { navigateToAgentEditor } = useNavigateToAgentsEditor();

    useEffect(() => {
        if (isOpen) {
            mixpanel?.track('missing-features', { event: 'click' });
        }
    }, [isOpen]);

    const onMaintenanceMode = async () => {
        if (agent) {
            await updateAgentMutation({ agentId: agent.id, update: { access: 'unlisted', embed: false } });

            navigateToEdit(true);
        }
    };

    const navigateToEdit = (wasEmbedded?: boolean) => {
        navigateToAgentEditor({ agentId: agent?.id as string, wasEmbedded });
    };

    return (
        <StyledDialog open={isOpen} onClose={onClose}>
            <CloseButton onClick={onClose} src={Close} size="16px" color="black" />
            <Title>
                <StyledIcon src={Warning} />
                <Text>Your Agent is live</Text>
            </Title>
            <MainText>
                To ensure users don't see real-time updates during editing, please enable Maintenance Mode while making changes.
            </MainText>
            <Title>
                <StyledButton variant="outlined" color="secondary" onClick={() => navigateToEdit(agent?.embed)} inverted>
                    Allow live changes
                </StyledButton>
                <StyledButton variant="outlined" color="secondary" onClick={onMaintenanceMode}>
                    Enable Maintenance Mode
                </StyledButton>
            </Title>
        </StyledDialog>
    );
};
